import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { createPathFromRoute } from 'assets/utils/dom/UrlParsing';
import { getConfig } from 'config';
import { ApiHeaderApplication } from 'config/Api.Config';
import { AuthPermission, claimsPermissions } from 'config/Auth.Config';
import { first } from 'lodash';
import { accountChangePasswordRoute } from 'module/account/views/changePassword/Account.ChangePassword.Index';
import { accountEditUserSettingsRoute } from 'module/account/views/editUserSettings/Account.EditUserSettings.Index';
import { accountResetPasswordRoute } from 'module/account/views/resetPassword/Account.ResetPassword.Index';
import { accountUserWelcomeRoute } from 'module/account/views/userWelcome/Account.UserWelcome.Index';
import { financeAccountReceivableIndexRoute } from 'module/finance/views/accountReceivable/Finance.AccountReceivable.Index';
import { financeClaimJobPaymentIndexRoute } from 'module/finance/views/claimJobPayment/Finance.ClaimJobPayment.Index';
import { financeClaimJobPaymentItemIndexRoute } from 'module/finance/views/claimJobPaymentItem/Finance.ClaimJobPaymentItem.Index';
import { financePostingAccountIndexRoute } from 'module/finance/views/postingAccount/Finance.PostingAccount.Index';
import { claimJobEmailTemplateIndexRoute } from 'module/management/views/claimJobEmailTemplate/ClaimJobEmailTemplate.Index';
import { claimJobPaymentEmailTemplateIndexRoute } from 'module/management/views/claimJobPaymentEmailTemplate/ClaimJobPaymentEmailTemplate.Index';
import { providerStatementBalanceEmailTemplateIndexRoute } from 'module/management/views/providerStatementBalanceEmailTemplate/ProviderStatementBalanceEmailTemplate.Index';
import { providerStatementEmailTemplateIndexRoute } from 'module/management/views/providerStatementEmailTemplate/ProviderStatementEmailTemplate.Index';
import { statementBalanceEmailTemplateIndexRoute } from 'module/management/views/statementBalanceEmailTemplate/StatementEmailTemplate.Index';
import { translationInfoIndexRoute } from 'module/management/views/translationInfo/TranslationInfo.Index';
import { pricingDistributionMasterTemplateIndexRoute } from 'module/productManagement/views/pricingDistributionMasterTemplate/PricingDistributionMasterTemplate.Index';
import { productEnhancementIndexRoute } from 'module/productManagement/views/productEnhancement/ProductEnhancement.Index';
import { providerIndexRoute } from 'module/productManagement/views/provider/Provider.Index';
import { refundConfigTemplateIndexRoute } from 'module/productManagement/views/refundConfigTemplate/RefundConfigTemplate.Index';
import { contractConsentIndexRoute } from 'module/sales/views/contractConsent/ContractConsent.Index';
import { salesContractRefundIndexRoute } from 'module/sales/views/contractRefund/Sales.ContractRefund.Index';
import { salesDraftIndexRoute } from 'module/sales/views/draft/Sales.Draft.Index';
import { salesQuoteIndexRoute } from 'module/sales/views/quote/Sales.Quote.Index';
import { statementBalanceIndexRoute } from 'module/sales/views/statementsBalance/StatementBalance.Index';
import { statementSummaryIndexRoute } from 'module/sales/views/statementSummary/StatementSummary.Index';
import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { claimIndexRoute } from '../../module/claims/views/claim/Claim.Index';
import { claimTypeIndexRoute } from '../../module/claims/views/claimType/ClaimType.Index';
import { contractIndexRoute } from '../../module/claims/views/contract/Contract.Index';
import { lossCodeIndexRoute } from '../../module/claims/views/lossCode/LossCode.Index';
import { payeeIndexRoute } from '../../module/claims/views/payee/Payee.Index';
import { payeeTypeIndexRoute } from '../../module/claims/views/payeeType/PayeeType.Index';
import { dealerIndexRoute } from '../../module/dealerManagement/views/dealer/Dealer.Index';
import { dealerGroupIndexRoute } from '../../module/dealerManagement/views/dealerGroup/DealerGroup.Index';
import { accessLogIndexRoute } from '../../module/management/views/accessLog/AccessLog.Index';
import { auditLogIndexRoute } from '../../module/management/views/auditLog/AuditLog.Index';
import { claimEmailTemplateIndexRoute } from '../../module/management/views/claimEmailTemplate/ClaimEmailTemplate.Index';
import { permissionIndexRoute } from '../../module/management/views/permission/Permission.Index';
import { roleIndexRoute } from '../../module/management/views/role/Role.Index';
import { statementEmailTemplateIndexRoute } from '../../module/management/views/statementEmailTemplate/StatementEmailTemplate.Index';
import { userIndexRoute } from '../../module/management/views/user/User.Index';
import { insurerIndexRoute } from '../../module/productManagement/views/insurer/Insurer.Index';
import { pricingDistributionTemplateIndexRoute } from '../../module/productManagement/views/pricingDistributionTemplate/PricingDistributionTemplate.Index';
import { pricingLimitTemplateIndexRoute } from '../../module/productManagement/views/pricingLimitTemplate/PricingLimitTemplate.Index';
import { productIndexRoute } from '../../module/productManagement/views/product/Product.Index';
import { productCategoryIndexRoute } from '../../module/productManagement/views/productCategory/ProductCategory.Index';
import { subfeeIndexRoute } from '../../module/productManagement/views/subfee/Subfee.Index';
import { tierIndexRoute } from '../../module/productManagement/views/tier/Tier.Index';
import { vehicleClassIndexRoute } from '../../module/productManagement/views/vehicleClass/VehicleClass.Index';
import { vehicleMakeIndexRoute } from '../../module/productManagement/views/vehicleMake/VehicleMake.Index';
import { vehicleModelIndexRoute } from '../../module/productManagement/views/vehicleModel/VehicleModel.Index';
import { salesContractIndexRoute } from '../../module/sales/views/contract/Sales.Contract.Index';
import { distributorIndexRoute } from '../../module/sales/views/distributor/Distributor.Index';
import { lenderIndexRoute } from '../../module/sales/views/lender/Lender.Index';
import { statementIndexRoute } from '../../module/sales/views/statements/Statement.Index';
import useCurrentApp from './useCurrentApp';

export function getRoutes() {
  const config = getConfig();
  const { lang } = getLocales();
  const routes: Array<Component.Navigation.Route> = [
    //Sales
    {
      icon: 'fas-file-contract',
      title: lang.contracts,
      permissions: [
        AuthPermission.editContract,
        AuthPermission.deleteContract,
        AuthPermission.createContract,
        AuthPermission.changeContractCreator,
        AuthPermission.updateContractStatus,
        AuthPermission.updateContractStatusUnrestricted,
        AuthPermission.restrictEditEffectiveDateContract,
        AuthPermission.editContractUnrestricted,
        AuthPermission.vertaforeSyncContract,
        AuthPermission.uploadDocumentContract,
        AuthPermission.sendContractForSigning,
        AuthPermission.reuploadSignedContractDocument,
        AuthPermission.editContractComment,
        AuthPermission.deleteContractComment,
        AuthPermission.viewContractComment,
        AuthPermission.viewContractLogs,
        AuthPermission.createStatementsFromContractList,
        AuthPermission.createContractRefund,
      ],
      applicationId: ApiHeaderApplication.sales,
      path: createPathFromRoute(salesContractIndexRoute, {}),
      route: salesContractIndexRoute,
    },
    {
      icon: 'fa-clipboard-question',
      title: lang.quote,
      permissions: [AuthPermission.editContract, AuthPermission.deleteContract, AuthPermission.createContract],
      applicationId: ApiHeaderApplication.sales,
      path: createPathFromRoute(salesQuoteIndexRoute, {}),
      route: salesQuoteIndexRoute,
    },
    {
      icon: 'fas-drafting-compass',
      title: lang.drafts,
      permissions: [AuthPermission.createContract],
      applicationId: ApiHeaderApplication.sales,
      path: createPathFromRoute(salesDraftIndexRoute, {}),
      route: salesDraftIndexRoute,
    },
    {
      icon: 'fas-certificate',
      title: lang.consents,
      permissions: [AuthPermission.resendContractConsent, AuthPermission.manuallyAcceptContractConsent],
      applicationId: ApiHeaderApplication.sales,
      path: createPathFromRoute(contractConsentIndexRoute, {}),
      route: contractConsentIndexRoute,
    },
    {
      icon: 'fas-building',
      title: lang.lenders,
      permissions: [AuthPermission.editLender, AuthPermission.deleteLender, AuthPermission.createLender],
      applicationId: ApiHeaderApplication.sales,
      path: createPathFromRoute(lenderIndexRoute, {}),
      route: lenderIndexRoute,
    },
    {
      icon: 'fas-users',
      title: lang.distributors,
      permissions: [AuthPermission.editDistributor, AuthPermission.deleteDistributor, AuthPermission.createDistributor],
      applicationId: ApiHeaderApplication.sales,
      path: createPathFromRoute(distributorIndexRoute, {}),
      route: distributorIndexRoute,
    },
    {
      icon: 'fas-file',
      title: lang.statements,
      permissions: [
        AuthPermission.createStatement,
        AuthPermission.editStatement,
        AuthPermission.deleteStatement,
        AuthPermission.updateStatementStatus,
        AuthPermission.modifyStatementEmail,
      ],
      applicationId: ApiHeaderApplication.sales,
      path: createPathFromRoute(statementIndexRoute, {}),
      route: statementIndexRoute,
    },
    {
      icon: 'fas-file-alt',
      title: lang.statementBalances,
      permissions: [
        AuthPermission.createStatementBalance,
        AuthPermission.editStatementBalance,
        AuthPermission.deleteStatementBalance,
        AuthPermission.modifyStatementBalanceEmail,
      ],
      applicationId: ApiHeaderApplication.sales,
      path: createPathFromRoute(statementBalanceIndexRoute, {}),
      route: statementBalanceIndexRoute,
    },
    {
      icon: 'fas-folder',
      title: lang.statementSummary,
      permissions: [AuthPermission.createStatementSummary, AuthPermission.deleteStatementSummary],
      applicationId: ApiHeaderApplication.sales,
      path: createPathFromRoute(statementSummaryIndexRoute, {}),
      route: statementSummaryIndexRoute,
    },
    {
      icon: 'fas-percentage',
      title: lang.refundQuotes,
      permissions: [
        AuthPermission.createContractRefund,
        AuthPermission.editContractRefund,
        AuthPermission.viewContractRefund,
      ],
      applicationId: ApiHeaderApplication.sales,
      path: createPathFromRoute(salesContractRefundIndexRoute, {}),
      route: salesContractRefundIndexRoute,
      hidden: !config.featureFlags?.isRefundEnabled,
    },

    //Claims
    {
      icon: 'fas-car-crash',
      title: lang.claims,
      permissions: claimsPermissions,
      applicationId: ApiHeaderApplication.claims,
      path: createPathFromRoute(claimIndexRoute, {}),
      route: claimIndexRoute,
    },
    {
      icon: 'fas-file-contract',
      title: lang.contracts,
      permissions: [
        AuthPermission.claimAdmin,
        AuthPermission.createClaim,
        AuthPermission.createClaimForExpiredContract,
        AuthPermission.createSimpleClaim,
        AuthPermission.editClaim,
        AuthPermission.deleteClaim,
        AuthPermission.editClaimCustomerInfo,
      ],
      applicationId: ApiHeaderApplication.claims,
      path: createPathFromRoute(contractIndexRoute, {}),
      route: contractIndexRoute,
    },
    {
      icon: 'fas-clipboard-list',
      title: lang.claimType,
      permissions: [AuthPermission.editClaimType, AuthPermission.deleteClaimType, AuthPermission.createClaimType],
      applicationId: ApiHeaderApplication.claims,
      path: createPathFromRoute(claimTypeIndexRoute, {}),
      route: claimTypeIndexRoute,
    },
    {
      icon: 'fas-list',
      title: lang.lossCode,
      permissions: [AuthPermission.editLossCode, AuthPermission.deleteLossCode, AuthPermission.createLossCode],
      applicationId: ApiHeaderApplication.claims,
      path: createPathFromRoute(lossCodeIndexRoute, {}),
      route: lossCodeIndexRoute,
    },
    {
      icon: 'fas-coins',
      title: lang.payeeType,
      permissions: [AuthPermission.editPayee, AuthPermission.deletePayee, AuthPermission.createPayee],
      applicationId: ApiHeaderApplication.claims,
      path: createPathFromRoute(payeeTypeIndexRoute, {}),
      route: payeeTypeIndexRoute,
    },
    {
      icon: 'fas-hand-holding-usd',
      title: lang.payee,
      permissions: [AuthPermission.editPayee, AuthPermission.deletePayee, AuthPermission.createPayee],
      applicationId: ApiHeaderApplication.claims,
      path: createPathFromRoute(payeeIndexRoute, {}),
      route: payeeIndexRoute,
    },

    //Finance
    {
      icon: 'fas-car-crash',
      title: lang.paymentsPending,
      permissions: [AuthPermission.viewClaimJobPaymentItem],
      applicationId: ApiHeaderApplication.finance,
      path: createPathFromRoute(financeClaimJobPaymentItemIndexRoute, {}),
      route: financeClaimJobPaymentItemIndexRoute,
    },
    {
      icon: 'fas-folder',
      title: lang.paymentsHistory,
      permissions: [
        AuthPermission.createClaimJobPayment,
        AuthPermission.editClaimJobPaymentItem,
        AuthPermission.updateClaimJobPaymentItemUsBankPayment,
        AuthPermission.cancelClaimJobPaymentItemUsBankPayment,
        AuthPermission.cancelClaimJobPayment,
      ],
      applicationId: ApiHeaderApplication.finance,
      path: createPathFromRoute(financeClaimJobPaymentIndexRoute, {}),
      route: financeClaimJobPaymentIndexRoute,
    },
    {
      icon: 'fas-barcode',
      title: lang.postingAccount,
      permissions: [
        AuthPermission.createPostingAccount,
        AuthPermission.editPostingAccount,
        AuthPermission.deletePostingAccount,
      ],
      applicationId: ApiHeaderApplication.finance,
      path: createPathFromRoute(financePostingAccountIndexRoute, {}),
      route: financePostingAccountIndexRoute,
    },
    {
      icon: 'fas-file-invoice-dollar',
      title: lang.accountReceivables,
      permissions: [
        AuthPermission.createAccountReceivable,
        AuthPermission.editAccountReceivable,
        AuthPermission.deleteAccountReceivable,
        AuthPermission.importAccountReceivable,
      ],
      applicationId: ApiHeaderApplication.finance,
      path: createPathFromRoute(financeAccountReceivableIndexRoute, {}),
      route: financeAccountReceivableIndexRoute,
      hidden: !config.featureFlags?.isRefundEnabled,
    },

    //Management
    {
      icon: 'fas-users',
      title: lang.users,
      permissions: [
        AuthPermission.createUser,
        AuthPermission.editUser,
        AuthPermission.deleteUser,
        AuthPermission.resetPassword,
      ],
      applicationId: ApiHeaderApplication.management,
      path: createPathFromRoute(userIndexRoute, {}),
      route: userIndexRoute,
    },
    {
      icon: 'fas-user-shield',
      title: lang.roles,
      permissions: [AuthPermission.createRole, AuthPermission.editRole, AuthPermission.deleteRole],
      applicationId: ApiHeaderApplication.management,
      path: createPathFromRoute(roleIndexRoute, {}),
      route: roleIndexRoute,
    },
    {
      icon: 'fas-shield-alt',
      title: lang.permissions,
      permissions: [AuthPermission.viewPermission],
      applicationId: ApiHeaderApplication.management,
      path: createPathFromRoute(permissionIndexRoute, {}),
      route: permissionIndexRoute,
    },
    {
      icon: 'if-login',
      title: lang.accessLogs,
      permissions: [AuthPermission.viewAccessLogs],
      applicationId: ApiHeaderApplication.management,
      path: createPathFromRoute(accessLogIndexRoute, {}),
      route: accessLogIndexRoute,
    },
    {
      icon: 'if-database',
      title: lang.changeLogs,
      permissions: [AuthPermission.viewAuditLogs],
      applicationId: ApiHeaderApplication.management,
      path: createPathFromRoute(auditLogIndexRoute, {}),
      route: auditLogIndexRoute,
    },
    {
      icon: 'fas-envelope',
      title: lang.emailTemplates,
      permissions: [
        AuthPermission.createEmailTemplate,
        AuthPermission.editEmailTemplate,
        AuthPermission.deleteEmailTemplate,
      ],
      applicationId: ApiHeaderApplication.management,
      groups: [
        {
          icon: 'fas-envelope',
          title: lang.claim,
          permissions: [
            AuthPermission.createEmailTemplate,
            AuthPermission.editEmailTemplate,
            AuthPermission.deleteEmailTemplate,
          ],
          applicationId: ApiHeaderApplication.management,
          path: createPathFromRoute(claimEmailTemplateIndexRoute, {}),
          route: claimEmailTemplateIndexRoute,
        },
        {
          icon: 'fas-envelope',
          title: lang.claimJob,
          permissions: [
            AuthPermission.createEmailTemplate,
            AuthPermission.editEmailTemplate,
            AuthPermission.deleteEmailTemplate,
          ],
          applicationId: ApiHeaderApplication.management,
          path: createPathFromRoute(claimJobEmailTemplateIndexRoute, {}),
          route: claimJobEmailTemplateIndexRoute,
        },
        {
          icon: 'fas-envelope',
          title: lang.finance,
          permissions: [
            AuthPermission.createEmailTemplate,
            AuthPermission.editEmailTemplate,
            AuthPermission.deleteEmailTemplate,
          ],
          applicationId: ApiHeaderApplication.management,
          path: createPathFromRoute(claimJobPaymentEmailTemplateIndexRoute, {}),
          route: claimJobPaymentEmailTemplateIndexRoute,
        },
        {
          icon: 'fas-envelope',
          title: lang.statement,
          permissions: [
            AuthPermission.createEmailTemplate,
            AuthPermission.editEmailTemplate,
            AuthPermission.deleteEmailTemplate,
          ],
          applicationId: ApiHeaderApplication.management,
          path: createPathFromRoute(statementEmailTemplateIndexRoute, {}),
          route: statementEmailTemplateIndexRoute,
        },
        {
          icon: 'fas-envelope',
          title: lang.statementBalance,
          permissions: [
            AuthPermission.createEmailTemplate,
            AuthPermission.editEmailTemplate,
            AuthPermission.deleteEmailTemplate,
          ],
          applicationId: ApiHeaderApplication.management,
          path: createPathFromRoute(statementBalanceEmailTemplateIndexRoute, {}),
          route: statementBalanceEmailTemplateIndexRoute,
        },
        {
          icon: 'fas-envelope',
          title: lang.providerStatement,
          permissions: [
            AuthPermission.createEmailTemplate,
            AuthPermission.editEmailTemplate,
            AuthPermission.deleteEmailTemplate,
          ],
          applicationId: ApiHeaderApplication.management,
          path: createPathFromRoute(providerStatementEmailTemplateIndexRoute, {}),
          route: providerStatementEmailTemplateIndexRoute,
        },
        {
          icon: 'fas-envelope',
          title: lang.providerStatementBalance,
          permissions: [
            AuthPermission.createEmailTemplate,
            AuthPermission.editEmailTemplate,
            AuthPermission.deleteEmailTemplate,
          ],
          applicationId: ApiHeaderApplication.management,
          path: createPathFromRoute(providerStatementBalanceEmailTemplateIndexRoute, {}),
          route: providerStatementBalanceEmailTemplateIndexRoute,
        },
      ],
    },

    //Product management
    {
      icon: 'fas-box',
      title: lang.products,
      permissions: [
        AuthPermission.editProduct,
        AuthPermission.deleteProduct,
        AuthPermission.createProduct,
        AuthPermission.editProductTax,
        AuthPermission.viewProductCustomPricePoint,
        AuthPermission.createProductCustomPricePoint,
        AuthPermission.editProductCustomPricePoint,
        AuthPermission.deleteProductCustomPricePoint,
        AuthPermission.viewProductNonWarrantyPricePoint,
        AuthPermission.createProductNonWarrantyPricePoint,
        AuthPermission.editProductNonWarrantyPricePoint,
        AuthPermission.deleteProductNonWarrantyPricePoint,
        AuthPermission.viewProductTierPricePoint,
        AuthPermission.createProductTierPricePoint,
        AuthPermission.editProductTierPricePoint,
        AuthPermission.deleteProductTierPricePoint,
        AuthPermission.viewProductClassPricePoint,
        AuthPermission.createProductClassPricePoint,
        AuthPermission.editProductClassPricePoint,
        AuthPermission.deleteProductClassPricePoint,
      ],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(productIndexRoute, {}),
      route: productIndexRoute,
    },
    {
      icon: 'fas-boxes',
      title: lang.productEnhancements,
      permissions: [
        AuthPermission.editProduct,
        AuthPermission.deleteProduct,
        AuthPermission.createProduct,
        AuthPermission.viewProductCustomPricePoint,
        AuthPermission.createProductCustomPricePoint,
        AuthPermission.editProductCustomPricePoint,
        AuthPermission.deleteProductCustomPricePoint,
        AuthPermission.viewProductNonWarrantyPricePoint,
        AuthPermission.createProductNonWarrantyPricePoint,
        AuthPermission.editProductNonWarrantyPricePoint,
        AuthPermission.deleteProductNonWarrantyPricePoint,
        AuthPermission.viewProductTierPricePoint,
        AuthPermission.createProductTierPricePoint,
        AuthPermission.editProductTierPricePoint,
        AuthPermission.deleteProductTierPricePoint,
        AuthPermission.viewProductClassPricePoint,
        AuthPermission.createProductClassPricePoint,
        AuthPermission.editProductClassPricePoint,
        AuthPermission.deleteProductClassPricePoint,
      ],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(productEnhancementIndexRoute, {}),
      route: productEnhancementIndexRoute,
    },
    {
      icon: 'fas-list',
      title: lang.programs,
      permissions: [AuthPermission.editProduct, AuthPermission.deleteProduct, AuthPermission.createProduct],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(productCategoryIndexRoute, {}),
      route: productCategoryIndexRoute,
    },
    {
      icon: 'fas-building',
      title: lang.providers,
      permissions: [AuthPermission.createProvider, AuthPermission.editProvider, AuthPermission.deleteProvider],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(providerIndexRoute, {}),
      route: providerIndexRoute,
    },
    {
      icon: 'fas-layer-group',
      title: lang.tiers,
      permissions: [AuthPermission.editTier, AuthPermission.deleteTier, AuthPermission.createTier],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(tierIndexRoute, {}),
      route: tierIndexRoute,
    },
    {
      icon: 'fas-coins',
      title: lang.limits,
      permissions: [
        AuthPermission.editPricingLimitTemplate,
        AuthPermission.deletePricingLimitTemplate,
        AuthPermission.createPricingLimitTemplate,
      ],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(pricingLimitTemplateIndexRoute, {}),
      route: pricingLimitTemplateIndexRoute,
    },
    {
      icon: 'fas-balance-scale',
      title: lang.pricingDistributionTemplates,
      permissions: [
        AuthPermission.editPricingDistributionTemplate,
        AuthPermission.deletePricingDistributionTemplate,
        AuthPermission.createPricingDistributionTemplate,
      ],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(pricingDistributionTemplateIndexRoute, {}),
      route: pricingDistributionTemplateIndexRoute,
    },
    {
      icon: 'fas-table',
      title: lang.pricingDistributionMasterTemplates,
      permissions: [AuthPermission.accessPricingDistributionMasterTemplate],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(pricingDistributionMasterTemplateIndexRoute, {}),
      route: pricingDistributionMasterTemplateIndexRoute,
    },
    {
      icon: 'fas-car',
      title: lang.vehicleMakes,
      permissions: [
        AuthPermission.editVehicleMake,
        AuthPermission.deleteVehicleMake,
        AuthPermission.createVehicleMake,
        AuthPermission.importVehicleMakeFromExcel,
      ],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(vehicleMakeIndexRoute, {}),
      route: vehicleMakeIndexRoute,
    },
    {
      icon: 'fas-car',
      title: lang.vehicleModels,
      permissions: [
        AuthPermission.editVehicleModel,
        AuthPermission.deleteVehicleModel,
        AuthPermission.createVehicleModel,
      ],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(vehicleModelIndexRoute, {}),
      route: vehicleModelIndexRoute,
    },
    {
      icon: 'fas-oil-can',
      title: lang.vehicleClasses,
      permissions: [
        AuthPermission.editVehicleClass,
        AuthPermission.deleteVehicleClass,
        AuthPermission.createVehicleClass,
      ],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(vehicleClassIndexRoute, {}),
      route: vehicleClassIndexRoute,
    },
    {
      icon: 'fas-users',
      title: lang.insurers,
      permissions: [AuthPermission.editInsurer, AuthPermission.deleteInsurer, AuthPermission.createInsurer],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(insurerIndexRoute, {}),
      route: insurerIndexRoute,
    },
    {
      icon: 'fas-plus',
      title: lang.surcharges,
      permissions: [AuthPermission.editSubfee, AuthPermission.deleteSubfee, AuthPermission.createSubfee],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(subfeeIndexRoute, {}),
      route: subfeeIndexRoute,
    },
    {
      icon: 'fas-layer-group',
      title: lang.refundConfigTemplates,
      permissions: [
        AuthPermission.editRefundConfigTemplate,
        AuthPermission.deleteRefundConfigTemplate,
        AuthPermission.createRefundConfigTemplate,
      ],
      applicationId: ApiHeaderApplication.productManagement,
      path: createPathFromRoute(refundConfigTemplateIndexRoute, {}),
      route: refundConfigTemplateIndexRoute,
    },

    //Dealer management
    {
      icon: 'fas-user-friends',
      title: lang.dealers,
      applicationId: ApiHeaderApplication.dealerManagement,
      permissions: [
        AuthPermission.editDealer,
        AuthPermission.deleteDealer,
        AuthPermission.createDealer,
        AuthPermission.restrictDeleteDealerDocuments,
      ],
      path: createPathFromRoute(dealerIndexRoute, {}),
      route: dealerIndexRoute,
    },
    {
      icon: 'fas-users',
      title: `${lang.dealer} ${lang.groups}`,
      permissions: [AuthPermission.editDealerGroup, AuthPermission.deleteDealerGroup, AuthPermission.createDealerGroup],
      applicationId: ApiHeaderApplication.dealerManagement,
      path: createPathFromRoute(dealerGroupIndexRoute, {}),
      route: dealerGroupIndexRoute,
    },

    //Extra hidden routes
    {
      icon: 'fas-user',
      title: lang.accountInfo,
      permissions: [],
      applicationId: ApiHeaderApplication.management,
      path: createPathFromRoute(accountUserWelcomeRoute, {}),
      route: accountUserWelcomeRoute,
      hidden: true,
    },
    {
      icon: 'fas-lock',
      title: lang.resetPassword,
      permissions: [],
      applicationId: ApiHeaderApplication.management,
      path: createPathFromRoute(accountResetPasswordRoute, {}),
      route: accountResetPasswordRoute,
      hidden: true,
    },
    {
      icon: 'fas-lock',
      title: lang.changePassword,
      permissions: [],
      applicationId: ApiHeaderApplication.management,
      path: createPathFromRoute(accountChangePasswordRoute, {}),
      route: accountChangePasswordRoute,
      hidden: true,
    },
    {
      icon: 'fas-user',
      title: lang.editMySettings,
      permissions: [],
      applicationId: ApiHeaderApplication.management,
      path: createPathFromRoute(accountEditUserSettingsRoute, {}),
      route: accountEditUserSettingsRoute,
      hidden: true,
    },
    {
      icon: 'fas-globe',
      title: lang.language,
      permissions: [],
      applicationId: ApiHeaderApplication.management,
      path: createPathFromRoute(translationInfoIndexRoute, {}),
      route: translationInfoIndexRoute,
      hidden: true,
    },
  ];

  return routes;
}

export default function useRouter() {
  const allRoutes = getRoutes();
  const authInfo = useAuthStore();
  const currentApp = useCurrentApp();
  const history = useHistory();
  const location = useLocation();
  const routesWithAccess = allRoutes.filter((it) => !it.permissions.length || authInfo.hasAccess(...it.permissions));
  const routesWithAccessForCurrentApp = routesWithAccess.filter((it) => it.applicationId === currentApp.id);
  let homeRoute = first(routesWithAccess);
  if (homeRoute?.groups && !homeRoute?.path) {
    homeRoute = first(homeRoute?.groups);
  }

  useEffect(() => {
    if (location.pathname === '/') {
      history.push(homeRoute?.path);
    }
  }, [location.pathname]);

  return {
    allRoutes: routesWithAccess,
    routes: routesWithAccessForCurrentApp,
    homeRoute,
  };
}
